import { css } from 'aphrodite';
import React from 'react';
import { ISelectBoxOption, SelectBox } from '../../../../../../components/SelectBox';
import { QuestionType } from '../questionTypes';
import { styleSheet } from '../styles';

interface FieldTypeSelectorProps {
	/** The QuestionType currently selected — used to highlight the right option. */
	selectedType: QuestionType;
	questionIndex: number;
	isEdit: boolean;
	hasBeenSaved: boolean;
	disabledInputs: boolean;
	formFieldOptions: Array<{ title: string; value: QuestionType }>;
	onRenderTriggerTitle?: (selectedOption: ISelectBoxOption<QuestionType>) => React.ReactNode;
	onSelectionChanged: (selectedType: ISelectBoxOption<QuestionType>, questionIndex: number) => void;
}

export const FieldTypeSelector = React.memo<FieldTypeSelectorProps>(
	({
		selectedType,
		questionIndex,
		isEdit,
		hasBeenSaved,
		disabledInputs,
		formFieldOptions,
		onRenderTriggerTitle,
		onSelectionChanged,
	}) => {
		// Validate required props early to fail fast
		if (!formFieldOptions) {
			throw new Error('FieldTypeSelector: formFieldOptions prop is required');
		}

		// Memoize expensive calculations
		const isDisabled = React.useMemo(
			() => (isEdit && hasBeenSaved) || disabledInputs,
			[isEdit, hasBeenSaved, disabledInputs]
		);

		const selectedOption = React.useMemo(
			() => formFieldOptions.find(x => x.value === selectedType),
			[formFieldOptions, selectedType]
		);

		// Memoize trigger className
		const triggerClassName = React.useMemo(
			() => css(styleSheet.dropdownTrigger, isDisabled ? styleSheet.dropdownTriggerDisabled : undefined),
			[isDisabled]
		);

		// Memoize menu className
		const menuClassName = React.useMemo(() => `${css(styleSheet.dropdownMenu)}`, []);

		// Memoize selection handler
		const handleSelectionChanged = React.useCallback(
			(selectedOpt: ISelectBoxOption<QuestionType>) => onSelectionChanged(selectedOpt, questionIndex),
			[onSelectionChanged, questionIndex]
		);

		return (
			<SelectBox
				disabled={isDisabled}
				menuClassName={menuClassName}
				onRenderSelectedOptionTitle={onRenderTriggerTitle}
				onSelectionChanged={handleSelectionChanged}
				options={formFieldOptions}
				selectedOption={selectedOption}
				triggerClassName={triggerClassName}
			/>
		);
	}
);

FieldTypeSelector.displayName = 'FieldTypeSelector';
